/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useMemo } from 'react'

import { EuiTitle, EuiSpacer, EuiDescriptionList } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import SelectTrafficFilter from './SelectTrafficFilter'
import DescriptionDetail from './details/DescriptionDetail'
import ProviderDetail from './details/ProviderDetail'
import RegionDetail from './details/RegionDetail'
import IncludedByDefaultDetail from './details/IncludedByDefaultDetail'

import type { TrafficFilterAssociationHandlerChildProps } from './types'

const PrivateConnectionForm: React.FunctionComponent<TrafficFilterAssociationHandlerChildProps> = ({
  rulesets,
  rulesetId,
  setRulesetId,
}) => {
  const filteredRulesets = useMemo(
    () => rulesets.filter((ruleset) => ruleset.type !== 'ip'),
    [rulesets],
  )

  const selectedRuleset = useMemo(
    () => filteredRulesets.find((ruleset) => ruleset.id === rulesetId),
    [filteredRulesets, rulesetId],
  )

  return (
    <Fragment>
      <SelectTrafficFilter
        rulesetType='privateConnection'
        rulesets={filteredRulesets}
        value={rulesetId}
        onChange={setRulesetId}
      />

      {selectedRuleset && (
        <Fragment>
          <EuiSpacer size='xxl' />

          <EuiTitle size='s'>
            <h3>
              <FormattedMessage
                id='privateConnectionForm.detailsTitle'
                defaultMessage='Details for {name}'
                values={{ name: selectedRuleset.name }}
              />
            </h3>
          </EuiTitle>

          <EuiSpacer size='l' />

          <EuiDescriptionList rowGutterSize='m' compressed={true}>
            <DescriptionDetail ruleset={selectedRuleset} />
            <ProviderDetail ruleset={selectedRuleset} />
            <RegionDetail ruleset={selectedRuleset} />
            <IncludedByDefaultDetail ruleset={selectedRuleset} />
          </EuiDescriptionList>
        </Fragment>
      )}
    </Fragment>
  )
}

export default PrivateConnectionForm
